/**
 * Manifest schemas (Zod). Every workspace member that consumes the
 * platform-ops catalog (lexiun-platform-ops plugin, LexIDE registry,
 * future lexserver canonical mirror) validates against these schemas.
 *
 * Two top-level shipped artifacts:
 *  - `catalog.json` — list of agent-invokable operations, validated by
 *     `CatalogSchema`.
 *  - `prompts/prompt-manifest.json` — index of the external LLM prompt
 *     template files, validated by `PromptManifestSchema`.
 *
 * Versioning:
 *  - `schema_version` (literal 1 for now) bumps on breaking SHAPE
 *    changes. Discriminated union when v2 lands.
 *  - `catalog_version` / `prompts_version` (semver string) bump on
 *    content changes. Drives the Layer-2 runtime handshake — see the
 *    author/operator guide §6.2.
 *
 * Pattern mirrors `@lexiun/harn-sdk/manifest`'s discriminated-union +
 * versioned-shape approach. See ai-docs/lexiun-platform-llm-tools-guide.md
 * for the design rationale.
 */
import { z } from "zod";
// ── Targets + roles ─────────────────────────────────────────────────────────
/**
 * Discriminator that picks which dispatcher executes the operation.
 *  - `lexserver_rest` — internal HTTP from the runtime to lexserver.
 *  - `lexruntime_rpc` — in-process delegation to an existing gateway RPC.
 *  - `ide_client` — server-push EventFrame → SPA handler → RPC result.
 *
 * Snake_case literals — matches the `harn-sdk` manifest style
 * (`execution.mode: "in-process"` etc.) and avoids the kebab/dot
 * inconsistency the design phase considered.
 */
export const TargetSchema = z.enum(["lexserver_rest", "lexruntime_rpc", "ide_client"]);
/**
 * Roles the catalog can require. Stable enum strings — the LLM
 * translates them to natural language for the user via the prompt
 * template (see guide §3.6 placeholders).
 *
 *  - `any` — no role required (e.g. `ide.toast.show`).
 *  - `MEMBER` / `ORG_ADMIN` — org-level roles.
 *  - `case:any` / `case:OBSERVER` / `case:CONTRIBUTOR` / `case:LEAD`
 *    — per-case roles (any case role / read-only / can-mutate / lead).
 *  - `platform.admin` — operator-only scope (e.g. LLM-key rotation per
 *    decision 8 in the canonical design).
 */
export const RoleSchema = z.enum([
    "any",
    "MEMBER",
    "ORG_ADMIN",
    "case:any",
    "case:OBSERVER",
    "case:CONTRIBUTOR",
    "case:LEAD",
    "platform.admin",
]);
// ── Shared fields across all operation entries ──────────────────────────────
//
// Common shape regardless of `target`. Authz, descriptive, and
// lifecycle fields live here. Target-specific fields are added per
// branch below via the discriminated union.
const SharedOperationFields = {
    /** Tool name surfaced to the LLM, e.g. `platform.org.user.create`. */
    name: z.string().min(1),
    /** UI/admin grouping label, e.g. `user-management`. Free-form. */
    category: z.string().min(1),
    /** Short human label, e.g. `Create org user`. */
    label: z.string().min(1),
    /** One-sentence description shown to the LLM in the tool roster. */
    description: z.string().min(1),
    /**
     * JSON Schema for the operation's params. Feeds both the LLM's
     * structured tool input + the SPA-side Zod validator (defense in
     * depth). Loose typing here — JSON Schema is rich and we don't want
     * to constrain it at the manifest layer.
     */
    input_schema: z.record(z.string(), z.unknown()).optional(),
    /** JWT scopes required, e.g. `["org.update"]`. Empty/absent → no scope check. */
    required_scopes: z.array(z.string()).default([]),
    /** Role required (org-level or per-case). Absent → `"any"`. */
    required_role: RoleSchema.default("any"),
    /** True for destructive ops (delete, archive, deprovision). Triggers `confirms` + audit emphasis. */
    destructive: z.boolean().default(false),
    /** True if SPA should show a confirmation modal before the dispatcher executes. */
    confirms: z.boolean().default(false),
    /** True if the op's result includes an `undo_token` + a paired `<name>.undo` catalog entry exists. */
    undoable: z.boolean().default(false),
    /** Op is being retired — still callable but filtered out of system-prompt advertisement. */
    deprecated: z.boolean().default(false),
    /** Human-readable deprecation reason. Surfaced in advertisements + deprecation warnings. */
    deprecation_reason: z.string().optional(),
    /** Old names that still resolve to this op for one major-version cycle. */
    aliases: z.array(z.string()).default([]),
};
// ── Target-specific operation variants ──────────────────────────────────────
const RestTargetFields = z.object({
    target: z.literal("lexserver_rest"),
    rest: z.object({
        /** HTTP verb. */
        method: z.enum(["GET", "POST", "PATCH", "PUT", "DELETE"]),
        /** Path with `{placeholder}` interpolation, e.g. `/api/org/{orgId}/users`. */
        path: z.string().min(1),
    }),
});
const RpcTargetFields = z.object({
    target: z.literal("lexruntime_rpc"),
    rpc: z.object({
        /** Name of an existing gateway RPC method to delegate to, e.g. `case.create`. */
        method: z.string().min(1),
    }),
});
const IdeClientTargetFields = z.object({
    target: z.literal("ide_client"),
    // No extra fields — routing is over the gateway WS to the connected SPA.
});
/**
 * Discriminated union by `target`. Each variant carries the shared
 * fields plus its target-specific block.
 */
export const PlatformOpSchema = z.discriminatedUnion("target", [
    z.object({ ...SharedOperationFields, ...RestTargetFields.shape }),
    z.object({ ...SharedOperationFields, ...RpcTargetFields.shape }),
    z.object({ ...SharedOperationFields, ...IdeClientTargetFields.shape }),
]);
// ── Catalog top-level ───────────────────────────────────────────────────────
/** Semver-string regex — `MAJOR.MINOR.PATCH` with optional pre-release/build suffix. */
const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;
export const CatalogSchema = z.object({
    /** Manifest shape version. Currently always 1; bump on shape changes. */
    schema_version: z.literal(1),
    /** Content version (semver). Bumps on every catalog change; drives Layer-2 handshake. */
    catalog_version: z.string().regex(SEMVER, "catalog_version must be semver MAJOR.MINOR.PATCH"),
    /** The operations themselves. Order is not significant. */
    operations: z.array(PlatformOpSchema),
});
// ── Prompt manifest (separate from the catalog so prompts iterate independently)
export const PromptManifestSchema = z.object({
    /** Prompt-manifest shape version. */
    schema_version: z.literal(1),
    /** Content version of the prompt template (semver). Bumps on prompt wording changes. */
    prompts_version: z.string().regex(SEMVER, "prompts_version must be semver MAJOR.MINOR.PATCH"),
    /**
     * Relative paths from `prompts/` to the three template files. Loaded
     * at plugin init by `services/prompt-template.ts`. There is exactly
     * ONE template (no locale variants) — the LLM produces user-facing
     * output in the user's input language per the language-matching rule
     * embedded in every template file. See guide §3.6.
     */
    files: z.object({
        /** Main template. References the partials via `{{partial:*}}`. */
        system_prompt: z.string().min(1),
        /** Partial: how to respond when the user asks for an op NOT in catalog. */
        unavailable_op: z.string().min(1),
        /** Partial: how to respond when `before_tool_call` denies a call. */
        unauthorized_op: z.string().min(1),
    }),
});
// ── Known placeholders (drives prompt-integrity test) ───────────────────────
/**
 * Placeholders the runtime's `before-prompt-build` hook substitutes
 * before appending the rendered template to the system prompt. The
 * `prompt-integrity.test.ts` validator asserts every `{{name}}` in
 * the three template files is in this set (catches typos like
 * `{{ops_avalable_list}}`).
 */
export const KNOWN_PROMPT_PLACEHOLDERS = new Set([
    "authorized_ops_list",
    "user_role",
    "required_role",
    "op_name",
    "op_description",
    // Partial-inclusion placeholders — handled specially by the renderer.
    "partial:unavailable-op",
    "partial:unauthorized-op",
]);
/**
 * The language-matching clause MUST appear in all three prompt files.
 * The validator checks for this exact string (substring match; allows
 * the surrounding context to vary). If a future revision rewords this,
 * update both the templates AND this constant in lockstep.
 */
export const LANGUAGE_MATCHING_CLAUSE_KEY = "Language matching rule.";
//# sourceMappingURL=schema.js.map